import React, { useState, useEffect, useMemo } from "react";
import { Box, Flex, Text, Button, ButtonGroup } from "@chakra-ui/react";
import { useLazyQuery } from "@apollo/client";
import {
  FETCH_OPERATOR_DAILY_STATS,
  FETCH_OPERATOR_WEEKLY_STATS,
  FETCH_OPERATOR_MONTHLY_STATS,
} from "utils/queries";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import ApexChart from "components/Charts/ApexChart";

const StatsChart = ({ operatorId }) => {
  const [chartData, setChartData] = useState([]);
  const [duration, setDuration] = useState("day");

  // Memoize timestamps to prevent infinite re-renders
  const timestamps = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const oneDaySeconds = 24 * 60 * 60;
    const oneWeekSeconds = 7 * oneDaySeconds;
    const oneMonthSeconds = 30 * oneDaySeconds;

    switch (duration) {
      case "week":
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneWeekSeconds,
        };
      case "month":
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneMonthSeconds,
        };
      default:
        // day
        return {
          timestamp_lt: now,
          timestamp_gt: now - oneDaySeconds,
        };
    }
  }, [duration]);

  const [
    getDailyStats,
    { data: dailyStatsData, loading: dailyLoading, error: dailyError },
  ] = useLazyQuery(FETCH_OPERATOR_DAILY_STATS);

  const [
    getWeeklyStats,
    { data: weeklyStatsData, loading: weeklyLoading, error: weeklyError },
  ] = useLazyQuery(FETCH_OPERATOR_WEEKLY_STATS);

  const [
    getMonthlyStats,
    { data: monthlyStatsData, loading: monthlyLoading, error: monthlyError },
  ] = useLazyQuery(FETCH_OPERATOR_MONTHLY_STATS);

  useEffect(() => {
    if (operatorId) {
      const variables = {
        operatorId: operatorId?.toString(),
        ...timestamps,
      };

      switch (duration) {
        case "week":
          getWeeklyStats({ variables });
          break;
        case "month":
          getMonthlyStats({ variables });
          break;
        default:
          getDailyStats({ variables });
      }
    }
  }, [operatorId, duration, timestamps, getDailyStats, getWeeklyStats, getMonthlyStats]);

  const transformedChartData = useMemo(() => {
    let statsData;
    switch (duration) {
      case "week":
        statsData = weeklyStatsData;
        break;
      case "month":
        statsData = monthlyStatsData;
        break;
      default:
        statsData = dailyStatsData;
    }

    if (!statsData?.operatorStats) return [];

    const sortedData = [...statsData.operatorStats]
      .sort((a, b) => parseInt(a.dynasty) - parseInt(b.dynasty))
      .map((stat) => {
        return {
          ...stat,
          stake: stat.stake ? parseFloat(stat.stake) / 10 ** 18 : 0,
        };
      });

    // Check if all stake values are the same
    const stakeValues = sortedData.map((item) => item.stake);
    const uniqueStakeValues = [...new Set(stakeValues)];
    const allStakesEqual = uniqueStakeValues.length === 1 && uniqueStakeValues[0] !== 0;

    // If all stakes are equal, add tiny variations to make the line visible
    if (allStakesEqual) {
      const baseStake = uniqueStakeValues[0];
      return sortedData.map((stat, index) => {
        // Add a very small variation (0.001%) alternating around the base value
        const variation = (index % 2 === 0 ? 1 : -1) * baseStake * 0.00001;
        return {
          ...stat,
          stake: stat.stake + variation,
        };
      });
    }

    return sortedData;
  }, [dailyStatsData, weeklyStatsData, monthlyStatsData, duration]);

  useEffect(() => {
    if (transformedChartData) {
      setChartData(transformedChartData);
    }
  }, [transformedChartData]);

  const loading =
    duration === "week"
      ? weeklyLoading
      : duration === "month"
      ? monthlyLoading
      : dailyLoading;
  const error =
    duration === "week"
      ? weeklyError
      : duration === "month"
      ? monthlyError
      : dailyError;

  // Prepare data for ApexCharts with multiple series
  const chartSeries = useMemo(() => {
    return [
      {
        name: "Commits",
        type: "line",
        data: chartData.map((item) => ({
          x: parseInt(item.dynasty),
          y: parseInt(item.numCommits || 0),
          timestamp: item.timestamp,
        })),
      },

      {
        name: "Reveals",
        type: "line",
        data: chartData.map((item) => ({
          x: parseInt(item.dynasty),
          y: parseInt(item.numReveals || 0),
          timestamp: item.timestamp,
        })),
      },
            {
        name: "Stake",
        type: "line",
        data: chartData.map((item) => ({
          x: parseInt(item.dynasty),
          y: parseFloat(item.stake || 0),
          timestamp: item.timestamp,
        })),
      },
    ];
  }, [chartData]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        id: "operator-stats-chart",
        type: "line",
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        zoom: {
          enabled: true,
          type: "x",
          autoScaleYaxis: true,
        },
      },
      stroke: {
        width: [3, 3, 3],
        curve: "smooth",
      },
      colors: ["#00E5CC", "#9D6CFF", "#FF6B47"],
      yaxis: {
        title: {
          text: "Number of Requests",
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        labels: {
          formatter: function (value) {
            if (value === 0) return "0";
            if (value >= 1000000) {
              return (value / 1000000).toFixed(1) + "M";
            } else if (value >= 1000) {
              return (value / 1000).toFixed(1) + "K";
            }
            return Math.floor(value).toString();
          },
        },
        forceNiceScale: true,
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0, // Hide markers by default for cleaner look
        hover: {
          size: 6,
          sizeOffset: 3,
        },
      },
      xaxis: {
        type: "numeric",
        title: {
          text: "Dynasty",
          style: {
            fontSize: "14px",
            fontWeight: 600,
          },
        },
        labels: {
          formatter: function (value) {
            return Math.floor(value);
          },
          rotate: 0,
          hideOverlappingLabels: true,
        },
        tickAmount: 10, // Limit number of ticks on X-axis
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function ({ series, dataPointIndex, w }) {
          const data = w.config.series[0].data[dataPointIndex];
          const timestamp = data?.timestamp;
          const dateTime = timestamp
            ? new Date(parseInt(timestamp) * 1000).toLocaleString()
            : "";
          const dynasty = data?.x || 0;

          const commits = series[0][dataPointIndex] || 0;
          const reveals = series[1][dataPointIndex] || 0;
          const stake = series[2][dataPointIndex] || 0;

          const formatStake = (val) => {
            if (val >= 1000000) {
              return (val / 1000000).toFixed(2) + " M";
            } else if (val >= 1000) {
              return (val / 1000).toFixed(2) + " K";
            }
            return val.toFixed(2);
          };

          return `
            <div style="padding: 12px; background: #0f1535; border: 1px solid #56577A; border-radius: 6px; min-width: 200px;">
              <div style="color: #fff; font-weight: 600; margin-bottom: 8px; font-size: 14px;">
                Dynasty ${dynasty}
              </div>
              <div style="color: #a0aec0; font-size: 12px; margin-bottom: 12px;">
                ${dateTime}
              </div>
              <div style="display: flex; flex-direction: column; gap: 6px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background: #00E5CC; border-radius: 2px;"></div>
                  <span style="color: #a0aec0; font-size: 12px;">Commits:</span>
                  <span style="color: #00E5CC; font-weight: 600; margin-left: auto;">${Math.floor(commits).toLocaleString()}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background: #9D6CFF; border-radius: 2px;"></div>
                  <span style="color: #a0aec0; font-size: 12px;">Reveals:</span>
                  <span style="color: #9D6CFF; font-weight: 600; margin-left: auto;">${Math.floor(reveals).toLocaleString()}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background: #FF6B47; border-radius: 2px;"></div>
                  <span style="color: #a0aec0; font-size: 12px;">Stake:</span>
                  <span style="color: #FF6B47; font-weight: 600; margin-left: auto;">${formatStake(stake)}</span>
                </div>
              </div>
            </div>
          `;
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "center",
        offsetY: 10,
        fontSize: "14px",
        fontWeight: 500,
        markers: {
          width: 12,
          height: 12,
          radius: 2,
        },
        itemMargin: {
          horizontal: 15,
          vertical: 5,
        },
      },
      grid: {
        borderColor: "#56577A",
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          top: 0,
          right: 20,
          bottom: 0,
          left: 10,
        },
      },
    };
  }, []);

  if (error) {
    return (
      <Card overflowX={{ sm: "scroll", xl: "hidden" }} pb="0px">
        <CardBody>
          <Text color="red.400" textAlign="center">
            Error loading operator statistics. Please try again later.
          </Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card overflowX={{ sm: "scroll", xl: "hidden" }} mb={10}>
      <CardHeader p="6px 0px 22px 0px">
        <Flex
          justify="space-between"
          align="center"
          w="100%"
          px={{ base: 3, md: 6 }}
          flexDir={{ base: "column", sm: "row" }}
          gap={{ base: 3, sm: 0 }}
        >
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            Operator Statistics
          </Text>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme={duration === "day" ? "blue" : "gray"}
              onClick={() => setDuration("day")}
              bg={duration === "day" ? "blue.500" : "transparent"}
              color={duration === "day" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "day" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1D
            </Button>
            <Button
              colorScheme={duration === "week" ? "blue" : "gray"}
              onClick={() => setDuration("week")}
              bg={duration === "week" ? "blue.500" : "transparent"}
              color={duration === "week" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "week" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1W
            </Button>
            <Button
              colorScheme={duration === "month" ? "blue" : "gray"}
              onClick={() => setDuration("month")}
              bg={duration === "month" ? "blue.500" : "transparent"}
              color={duration === "month" ? "white" : "gray.400"}
              _hover={{
                bg: duration === "month" ? "blue.600" : "whiteAlpha.200",
              }}
            >
              1M
            </Button>
          </ButtonGroup>
        </Flex>
      </CardHeader>
      <CardBody>
        <Box p={{ base: 2, md: 4 }} minH="450px" width="100%">
          {loading ? (
            <Flex
              justify="center"
              align="center"
              h="400px"
              flexDirection="column"
              gap={4}
            >
              <Text color="gray.400" fontSize="lg">
                Loading statistics...
              </Text>
            </Flex>
          ) : chartData.length === 0 ? (
            <Flex justify="center" align="center" h="400px">
              <Text color="gray.400" fontSize="lg">
                No statistics available for this operator.
              </Text>
            </Flex>
          ) : (
            <ApexChart
              type="line"
              series={chartSeries}
              options={chartOptions}
              height="400px"
            />
          )}
        </Box>
      </CardBody>
    </Card>
  );
};

export default StatsChart;
